import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DIGEST_SECTIONS, type DigestSettings } from '@testflow/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { digestApi } from '../../../lib/digest-api.js';
import { defaultActivePlanId, sortActivePlans } from '../../../lib/plan-selection.js';
import { plansApi } from '../../../lib/plans-api.js';
import { useDigestHistory, useDigestSendNow } from '../../../lib/use-digest-runs.js';
import { useCurrentProject } from '../../../lib/use-project.js';

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

const inputClass =
  'w-full rounded-btn border border-[var(--border)] bg-bg-surface px-1.5 py-1 text-sm text-text-body';

function toggleIn<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

// Plan 7 / Task F4 (NOT-5-Admin): Zeitplan + Empfänger + Sections des Weekly Digest. Anders als
// die Ein-Feld-Toggles (Gamification/Begriff) hat der Digest mehrere zusammenhängende Felder —
// lokaler Entwurfszustand + expliziter "Speichern"-Button (wie VocabPanel/ExitCriteriaPanel), damit
// nicht jeder Klick auf eine Checkbox einen halbfertigen Zwischenstand an den Server schickt.
// Empfänger sind Projekt-Mitglieder (plansApi.members, Plan 4) — es gibt keine eigene
// "Digest-Empfänger"-Verwaltung. "Vorschau"/"Jetzt senden" übernehmen den Button-Wortlaut + das
// Iframe-Vorschaumuster aus Task C7 (digest-history.tsx); eine eigene Historie-Ansicht bleibt dort.
export function DigestPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id;

  const query = useQuery({
    queryKey: ['digest-settings', projectId],
    queryFn: () => digestApi.getSettings(projectId ?? ''),
    enabled: Boolean(projectId),
  });
  const membersQuery = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => plansApi.members(projectId ?? ''),
    enabled: Boolean(projectId),
  });
  const plansQuery = useQuery({
    queryKey: ['plans', projectId],
    queryFn: () => plansApi.list(projectId ?? ''),
    enabled: Boolean(projectId),
  });
  // Task 6 (#55): mehrere Testpläne dürfen gleichzeitig aktiv sein, also entscheidet nicht mehr
  // der ERSTE aktive Treffer in Listenreihenfolge (`find(is_active) ?? plans[0]`), sondern die
  // fachliche Vorrangregel aus plan-selection.ts (nächster Zieltermin) — dieselbe Regel wie auf
  // der Sign-off-Seite (signoff-page.tsx) und in der Digest-Historie (digest-history.tsx).
  const plans = plansQuery.data ?? [];
  const activePlans = sortActivePlans(plans);
  // #114 (A9): „nicht aktiv" umfasst jetzt Entwürfe UND abgeschlossene Pläne. Beide bleiben
  // wählbar — eine Digest-Auswahl ist kein Statement über den Lebenszyklus.
  const inactivePlans = plans.filter((p) => p.status !== 'aktiv');

  const [draft, setDraft] = useState<DigestSettings | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  // Die Vorschau nutzt den ERSTEN vom Nutzer gewählten Plan (Reihenfolge der Auswahl); ist keine
  // Auswahl getroffen (leere `test_plan_ids`, dieselbe Bedeutung wie im Digest-Versand selbst:
  // "alle aktiven Pläne"), fällt sie auf den fachlich vorrangigen aktiven Plan zurück.
  const previewPlanId = draft?.test_plan_ids[0] ?? defaultActivePlanId(plans);

  useEffect(() => {
    if (query.data) setDraft(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (settings: DigestSettings) => digestApi.putSettings(projectId ?? '', settings),
    meta: { successToast: 'feedback.saved' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['digest-settings', projectId] });
    },
  });

  // #160: antwortet sofort; der Toast unterscheidet gestartet / läuft schon / nicht versendet.
  const sendNow = useDigestSendNow(projectId);
  const historyQuery = useDigestHistory(projectId);
  const lastRun = [...(historyQuery.data ?? [])].sort((a, b) =>
    b.generated_at.localeCompare(a.generated_at),
  )[0];

  const preview = useMutation({
    mutationFn: () => digestApi.preview(projectId ?? '', previewPlanId ?? ''),
    onSuccess: ({ html }) => setPreviewHtml(html),
  });

  if (projectLoading) {
    return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  }
  if (!project || !projectId) {
    return <div className="p-6 text-text-muted">{t('catalog.noProject')}</div>;
  }
  if (query.isLoading || !draft) {
    return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  }

  function update(patch: Partial<DigestSettings>) {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }

  const members = membersQuery.data ?? [];

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-text-primary">{t('admin.digest.navLabel')}</h2>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => preview.mutate()}
            disabled={!previewPlanId || preview.isPending}
            className="rounded-btn border border-brand-accent px-3 py-1.5 text-sm text-brand-accent disabled:opacity-50"
          >
            {t('digest.preview')}
          </button>
          <button
            type="button"
            onClick={() => sendNow.mutate()}
            disabled={sendNow.isPending}
            className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-body disabled:opacity-50"
          >
            {t('digest.send')}
          </button>
          <button
            type="button"
            onClick={() => save.mutate(draft)}
            disabled={save.isPending}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t('admin.digest.save')}
          </button>
        </div>
      </div>

      {lastRun ? (
        <p className="text-xs text-text-muted">
          {t('digest.lastRun', {
            status: t(`digest.status.${lastRun.status}`),
            window: lastRun.window,
          })}{' '}
          ·{' '}
          <Link to="/reporting/digest" className="text-brand-accent hover:underline">
            {t('digest.toHistory')}
          </Link>
        </p>
      ) : null}

      <section className="space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <label className="flex items-center gap-3 text-sm text-text-body">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
          />
          <span>{t('admin.digest.enabledLabel')}</span>
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-text-muted">
            {t('admin.digest.weekday')}
            <select
              value={draft.weekday}
              onChange={(e) => update({ weekday: Number(e.target.value) })}
              className={inputClass}
            >
              {WEEKDAYS.map((day) => (
                <option key={day} value={day}>
                  {t(`admin.digest.weekdays.${day}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-text-muted">
            {t('admin.digest.time')}
            <input
              type="time"
              value={draft.time}
              onChange={(e) => update({ time: e.target.value })}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-text-muted">
            {t('admin.digest.timezone')}
            <input
              type="text"
              value={draft.timezone}
              onChange={(e) => update({ timezone: e.target.value })}
              className={inputClass}
            />
          </label>
        </div>
      </section>

      {/* #129: Tester-Digest — eigene Karte, weil er ein eigener Versand ist (werktags, nur
          Tester, Uhrzeit separat). Läuft über denselben Entwurf + „Speichern" wie der Wochenbericht. */}
      <section className="space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <h3 className="text-sm font-bold text-text-primary">{t('admin.digest.tester.title')}</h3>
        <label className="flex items-center gap-3 text-sm text-text-body">
          <input
            type="checkbox"
            checked={draft.tester_digest_enabled}
            onChange={(e) => update({ tester_digest_enabled: e.target.checked })}
          />
          <span>{t('admin.digest.tester.enabled')}</span>
        </label>
        <label className="flex max-w-[12rem] flex-col gap-1 text-xs font-semibold uppercase text-text-muted">
          {t('admin.digest.tester.time')}
          <input
            type="time"
            value={draft.tester_digest_time}
            onChange={(e) => update({ tester_digest_time: e.target.value })}
            className={inputClass}
          />
        </label>
        <p className="text-xs text-text-muted">{t('admin.digest.tester.hint')}</p>
      </section>

      <section className="space-y-2 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <h3 className="text-sm font-bold text-text-primary">{t('admin.digest.sections')}</h3>
        <div className="flex flex-wrap gap-4">
          {DIGEST_SECTIONS.map((section) => (
            <label key={section} className="flex items-center gap-2 text-sm text-text-body">
              <input
                type="checkbox"
                checked={draft.sections.includes(section)}
                onChange={() => update({ sections: toggleIn(draft.sections, section) })}
              />
              <span>{t(`reporting.${section}`)}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="space-y-2 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <h3 className="text-sm font-bold text-text-primary">{t('admin.digest.recipients')}</h3>
        {membersQuery.isLoading ? (
          <div className="text-sm text-text-muted">{t('common.loading')}</div>
        ) : null}
        {!membersQuery.isLoading && members.length === 0 ? (
          <div className="text-sm text-text-muted">{t('admin.digest.noMembers')}</div>
        ) : null}
        <div className="flex flex-wrap gap-4">
          {members.map((member) => (
            <label key={member.user_id} className="flex items-center gap-2 text-sm text-text-body">
              <input
                type="checkbox"
                checked={draft.recipient_user_ids.includes(member.user_id)}
                onChange={() =>
                  update({
                    recipient_user_ids: toggleIn(draft.recipient_user_ids, member.user_id),
                  })
                }
              />
              <span>{member.name}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="space-y-2 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <h3 className="text-sm font-bold text-text-primary">{t('admin.digest.plans')}</h3>
        <div className="flex flex-wrap gap-4">
          {activePlans.map((planItem) => (
            <label
              key={planItem.test_plan_id}
              className="flex items-center gap-2 text-sm text-text-body"
            >
              <input
                type="checkbox"
                checked={draft.test_plan_ids.includes(planItem.test_plan_id)}
                onChange={() =>
                  update({
                    test_plan_ids: toggleIn(draft.test_plan_ids, planItem.test_plan_id),
                  })
                }
              />
              <span>{planItem.name}</span>
            </label>
          ))}
        </div>
        {/* Inaktive Pläne bleiben wählbar (eine Auswahl ist kein Statement über den Status), stehen
            aber als abgesetzte Gruppe darunter, damit "aktive zuerst" nicht durch Zufall entsteht. */}
        {inactivePlans.length > 0 ? (
          <div className="space-y-2 border-t border-[var(--border)] pt-2">
            <h4 className="text-xs font-semibold uppercase text-text-muted">
              {t('admin.digest.inactivePlans')}
            </h4>
            <div className="flex flex-wrap gap-4">
              {inactivePlans.map((planItem) => (
                <label
                  key={planItem.test_plan_id}
                  className="flex items-center gap-2 text-sm text-text-muted"
                >
                  <input
                    type="checkbox"
                    checked={draft.test_plan_ids.includes(planItem.test_plan_id)}
                    onChange={() =>
                      update({
                        test_plan_ids: toggleIn(draft.test_plan_ids, planItem.test_plan_id),
                      })
                    }
                  />
                  <span>{planItem.name}</span>
                </label>
              ))}
            </div>
          </div>
        ) : null}
        <p className="text-xs text-text-muted">{t('admin.digest.allActivePlans')}</p>
      </section>

      {previewHtml !== null ? (
        <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-bold text-text-primary">{t('digest.preview')}</h3>
            <button
              type="button"
              onClick={() => setPreviewHtml(null)}
              aria-label={t('common.close')}
              className="text-xs text-text-muted hover:text-text-primary"
            >
              ✕
            </button>
          </div>
          <iframe title={t('digest.preview')} srcDoc={previewHtml} className="h-[600px] w-full" />
        </section>
      ) : null}
    </div>
  );
}
